import React, { useEffect, useState } from "react";

import { RAW_ANNOTATION_TAG_NAMES } from "@perceptron/core/types";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { toast } from "sonner";

import { strings } from "@demo/utils/strings";

/** How long the button shows it has copied. */
const COPIED_MS = 2000;

/**
 * The answer as the reader would paste it: its markdown, with each annotation tag (coordinates, which mean
 * nothing outside the demo) replaced by what it refers to, its `mention`, or dropped when it names nothing.
 * Containers come first in the annotation list, so a box inside a collection goes with the collection.
 */
export function copyableAnswer(markdown: string): string {
  let text = markdown;
  for (const tag of RAW_ANNOTATION_TAG_NAMES) {
    const pattern = new RegExp(`<${tag}(\\s[^>]*)?>[\\s\\S]*?</${tag}>|<${tag}(\\s[^>]*)?/>`, "g");
    text = text.replace(pattern, (match) => /\smention="([^"]*)"/.exec(match)?.[1] ?? "");
  }
  return text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Copies the finished answer; the icon turns into a check for a moment once it has. */
export function CopyResponseButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyableAnswer(text));
      setCopied(true);
    } catch {
      toast.error(strings.agentOutput.copyResponseFailed);
    }
  };

  return (
    <Button
      aria-label={copied ? strings.agentOutput.copiedResponse : strings.agentOutput.copyResponse}
      className="size-8 shrink-0 p-0 text-foreground-secondary hover:text-foreground-primary"
      data-testid="copy-response"
      size="sm"
      title={strings.agentOutput.copyResponse}
      variant="ghost"
      onClick={copy}
    >
      <Icon className="size-4" name={copied ? "check" : "copy"} />
    </Button>
  );
}
